import { useId, useState } from 'react';
import {
  Box,
  FormControl,
  FormHelperText,
  FormLabel,
  InputAdornment,
  OutlinedInput,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

interface AmountQuestionProps {
  question: string;
  hint?: string;
  suffix?: string;
  value: number | undefined;
  onChange: (value: number | undefined) => void;
  required?: boolean;
}

const MAX_DIGITS = 15;

const parse = (text: string): number | undefined =>
  /^\d+$/.test(text) ? Number(text) : undefined;

const format = (value: number | undefined): string =>
  value === undefined ? '' : String(value);

export const AmountQuestion = ({
  question,
  hint,
  suffix,
  value,
  onChange,
  required,
}: AmountQuestionProps) => {
  const { t } = useTranslation();
  const labelId = useId();
  const hintId = useId();
  const errorId = useId();
  const [draft, setDraft] = useState(format(value));
  const [synced, setSynced] = useState(value);

  if (value !== synced) {
    setSynced(value);
    if (parse(draft) !== value) setDraft(format(value));
  }

  const error = draft !== '' && parse(draft) === undefined;

  const handleChange = (text: string) => {
    setDraft(text);
    onChange(parse(text));
  };

  return (
    <FormControl variant="standard" error={error} fullWidth sx={{ mb: 3 }}>
      <FormLabel
        id={labelId}
        sx={{
          typography: 'textMd',
          fontWeight: 600,
          color: 'text.primary',
          p: 0,
          mb: hint ? 0.5 : 1.5,
          '&.Mui-focused, &.Mui-error': { color: 'text.primary' },
        }}
      >
        {question}
        {required && (
          <Box component="span" sx={{ color: 'error.main', ml: 0.5 }}>
            *
          </Box>
        )}
      </FormLabel>
      {hint && (
        <Typography
          id={hintId}
          variant="textSm"
          sx={{ color: 'text.secondary', mb: 1.5, display: 'block' }}
        >
          {hint}
        </Typography>
      )}
      <OutlinedInput
        aria-describedby={
          [hint ? hintId : null, error ? errorId : null]
            .filter(Boolean)
            .join(' ') || undefined
        }
        value={draft}
        onChange={(event) => handleChange(event.target.value)}
        inputProps={{
          'aria-labelledby': labelId,
          inputMode: 'numeric',
          maxLength: MAX_DIGITS,
        }}
        endAdornment={
          suffix ? (
            <InputAdornment position="end">{suffix}</InputAdornment>
          ) : undefined
        }
        sx={{ maxWidth: 240, borderRadius: 1.5 }}
      />
      {error && (
        <FormHelperText id={errorId} sx={{ mt: 1, ml: 0 }}>
          {t('wizard.errors.amountInvalid')}
        </FormHelperText>
      )}
    </FormControl>
  );
};
